/**
 * UI store — Zustand.
 *
 * Layout / theme / density / command-palette state. The persisted subset
 * (sidebar collapsed + theme + density) is mirrored to localStorage by a
 * subscribe-style middleware below so behaviour matches the prior RTK
 * persistence middleware (and the original Zustand `persist` middleware
 * before that).
 *
 * Consumer API (preserved):
 *   const { sidebarCollapsed, toggleSidebar } = useUIStore();
 *   const theme = useUIStore((s) => s.theme);
 *   useUIStore.getState(); useUIStore.setState({...});
 */

import { create } from 'zustand';

export type Theme = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';

export interface UIState {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  theme: Theme;
  setTheme: (theme: Theme) => void;

  density: Density;
  setDensity: (density: Density) => void;

  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;
}

/** Storage key for the persisted UI subset. */
export const UI_PERSIST_KEY = 'nirc-ui-state';

/** Fields that survive a page reload. */
type PersistedUI = Pick<UIState, 'sidebarCollapsed' | 'theme' | 'density'>;

function loadPersistedUI(): Partial<PersistedUI> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(UI_PERSIST_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<PersistedUI>;
    return {
      sidebarCollapsed: parsed.sidebarCollapsed ?? false,
      theme: (parsed.theme ?? 'system'),
      density: (parsed.density ?? 'comfortable'),
    };
  } catch {
    // Corrupt localStorage entry — start fresh rather than crash boot.
    return {};
  }
}

const persisted = loadPersistedUI();

export const useUIStore = create<UIState>(
  (set: (partial: Partial<UIState> | ((s: UIState) => Partial<UIState>)) => void) => ({
    sidebarCollapsed: persisted.sidebarCollapsed ?? false,
    setSidebarCollapsed: (collapsed: boolean) => set({ sidebarCollapsed: collapsed }),
    toggleSidebar: () => set((s: UIState) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

    theme: persisted.theme ?? 'system',
    setTheme: (theme: Theme) => set({ theme }),

    density: persisted.density ?? 'comfortable',
    setDensity: (density: Density) => set({ density }),

    commandPaletteOpen: false,
    setCommandPaletteOpen: (open: boolean) => set({ commandPaletteOpen: open }),
    toggleCommandPalette: () =>
      set((s: UIState) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
  }),
);

/* ---------- Persistence: mirror the persisted subset to localStorage ---------- */

if (typeof window !== 'undefined') {
  useUIStore.subscribe((state: UIState) => {
    try {
      window.localStorage.setItem(
        UI_PERSIST_KEY,
        JSON.stringify({
          sidebarCollapsed: state.sidebarCollapsed,
          theme: state.theme,
          density: state.density,
        }),
      );
    } catch {
      // Quota or private mode — silently no-op. UI keeps working.
    }
  });
}
